import { AppDispatch, RootState } from '@configurations/store';
import { SettingsContext } from '@contexts/SettingsContext';
import { DebouncedFunc, debounce } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

// Redux/typescript utility hooks. Use these for typesafe inovcation of supported
// redux functions.
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

type TransformerFn<T> = (newVal: T) => T;
type ValueOrTransformerFn<T> = T | TransformerFn<T>;

export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  validateType?: (value: unknown) => value is T
): [T, (value: ValueOrTransformerFn<T>) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof globalThis === 'undefined') {
      return initialValue;
    }

    try {
      const item = globalThis.localStorage.getItem(key);
      if (!item) return initialValue;

      const parsedItem: unknown = JSON.parse(item);
      if (!validateType) return parsedItem as T;

      return validateType(parsedItem) ? parsedItem : initialValue;
    } catch {
      console.error(`Could not read ${key} from local storage.`);
      return initialValue;
    }
  });

  const setValue = (value: ValueOrTransformerFn<T>) => {
    try {
      const valueToStore =
        typeof value === 'function'
          ? (value as TransformerFn<T>)(storedValue)
          : value;

      setStoredValue(valueToStore);

      if (typeof globalThis !== 'undefined') {
        globalThis.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch {
      console.error(`Could not write ${key} to local storage.`);
    }
  };

  return [storedValue, setValue];
};

export function useDebouncedFn<T extends unknown[], U>(
  fn: (...args: T) => U,
  delay: number
): DebouncedFunc<(...args: T) => U> {
  const debouncedFuncRef = useRef(debounce(fn, delay));

  useEffect(() => {
    debouncedFuncRef.current.cancel();
    debouncedFuncRef.current = debounce(fn, delay);

    return () => {
      debouncedFuncRef.current.cancel();
    };
  }, [fn, delay]);

  // TODO: don't reference ref during render
  return debouncedFuncRef.current;
}

export const useFeatures = () => {
  const settings = useContext(SettingsContext);

  return settings.features;
};

export const useHasAllChannelsAccess = (campaignId: number) => {
  const { userType, campaign_ids: userCampaignIds } =
    useContext(SettingsContext);

  return userType === 'admin' || userCampaignIds.includes(campaignId);
};
